<template>
  <div class="mb-5">
    <div
      class="col-12 d-flex flex-wrap filter-row"
    >
      <div>
        <base-input :label="$t('COMMON.PER_PAGE')">
          <el-select
            class="select-primary pagination-select"
            v-model="pagination.perPage"
            :placeholder="$t('COMMON.PER_PAGE')"
          >
            <el-option
              class="select-primary"
              v-for="item in pagination.perPageOptions"
              :key="item"
              :label="item"
              :value="item"
            >
            </el-option>
          </el-select>
        </base-input>
      </div>
      <div>
        <base-input :label="$t('COMMON.SUPPLIERS')">
          <supplier-selector
            :multiple="false"
            :allowNone="false"
            :showAll="true"
            :placeholder="$t('COMMON.SUPPLIERS')"
            @supplierChanged="(supplier) => (selectedSupplier = supplier)"
          />
        </base-input>
      </div>
      <div>
        <base-input :label="$t('COMMON.CATEGORIES')">
          <category-group-selector
            :allowNone="false"
            :showAll="true"
            :isOnlyModels="false"
            :placeholder="$t('COMMON.CATEGORIES')"
            @categoryChanged="
              (categoryId) => {
                selectedCategory = categoryId;
              }
            "
          />
        </base-input>
      </div>
      <div>
        <base-input
          v-model="query"
          type="search"
          prepend-icon="fas fa-search"
          :placeholder="$t('COMMON.SEARCH_HINT.PRODUCT')"
          :label="$t('COMMON.SEARCH')"
          clearable
        />
      </div>
      <div v-if="isDraft">
        <base-checkbox v-model="displayZeroQuantities">
          <span class="form-control-label">
            {{ $t("COMMON.DISPLAY_ZERO_LINES") }}
          </span>
        </base-checkbox>
      </div>
    </div>

    <el-table
      class="table-responsive align-items-center table-flush"
      header-row-class-name="thead-light"
      :data="filteredProducts.slice(startIndex, endIndex)"
    >
      <div slot="empty" v-if="loading">
        <img src="/img/loading.gif" style="height: 100px; width: 100px" />
      </div>

      <el-table-column :label="$t('COMMON.CODE')" prop="code" min-width="200">
        <template v-slot="{ row }">
          <div v-if="row">
            <div style="display: flex; align-items: center; gap: 8px">
              <a href="#" @click.prevent="goToProduct(row)">
                {{ row.name }} {{ row.id }}
              </a>
            </div>
            <br />
            <div class="text-muted salesOrder-item-excerpt">
              <ul>
                <li>
                  {{ $t("PRODUCTS.CODE") }} :
                  {{ row.code }}
                </li>
                <li>
                  {{ $t("PRODUCTS.SKU") }} :
                  {{ row.sku }}
                </li>
              </ul>
            </div>
          </div>
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('COMMON.QUANTITY')"
        class-name="number-column"
        label-class-name="number-column"
      >
        <template v-slot="{ row }">
          <base-input
            :disabled="!isDraft"
            v-model="itemsQuantities[row.id]"
            type="number"
            placeholder="0"
            @change="
              (event) => {
                updateTransferItem(row, event.target.value);
              }
            "
          />
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('COMMON.UNIT')"
        class-name="number-column"
        label-class-name="number-column"
      >
        <template v-slot="{ row }">
          <object-link :object="row.unitOfMeasure?.referenceUnit" />
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('COMMON.UNIT_PRICE')"
        class-name="number-column"
        label-class-name="number-column"
      >
        <template v-slot="{ row }">
          {{ $formatCurrency(getProductUnitPrice(row), 4) }}
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('COMMON.SUBTOTAL')"
        class-name="number-column"
        label-class-name="number-column"
      >
        <template v-slot="{ row }">
          {{
            $formatCurrency(
              itemsByProduct[row.id]
                ? itemsByProduct[row.id].pricing.total_price ||
                    itemsByProduct[row.id].pricing.total
                : 0,
              4
            )
          }}
        </template>
      </el-table-column>
    </el-table>

    <div
      slot="footer"
      class="mt-5 col-12 d-flex justify-content-center justify-content-sm-between flex-wrap table-footer"
    >
      <div class="">
        <p class="card-category">
          {{
            $t("COMMON.DISPLAY_FROM_X_TO_X_OF_X_ENTRIES", {
              from: total ? from + 1 : 0,
              to: to,
              of: total,
            })
          }}
        </p>
      </div>
      <base-pagination
        class="pagination-no-border"
        v-model="pagination.currentPage"
        :per-page="pagination.perPage"
        :total="total"
      />
    </div>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option, Input } from "element-ui";
import { cloneDeep } from "lodash";
import SupplierSelector from "@/components/SupplierSelector.vue";
import CategoryGroupSelector from "@/components/CategoryGroupSelector.vue";
import ObjectLink from "@/components/ObjectLink.vue";
import defaultEstablishmentTransferItem from "../defaultEstablishmentTransferItem";

export default {
  name: "EstablishmentTransferItemsTable",
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [Input.name]: Input,
    SupplierSelector,
    CategoryGroupSelector,
    ObjectLink,
  },
  props: {
    transfer: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      query: "",
      selectedCategory: null,
      selectedSupplier: null,
      displayZeroQuantities: false,
      pagination: {
        perPage: 500,
        currentPage: 1,
        perPageOptions: [5, 10, 20, 50],
      },
      products: [],
      filteredProducts: [],
      itemsQuantities: {},
      itemsByProduct: {},
      productsSignatureLoaded: null,
      lastProductsFetchSignature: null,
      activeProductsRequestToken: null,
    };
  },
  computed: {
    isDraft() {
      return (this.transfer.status || "DRAFT") === "DRAFT";
    },
    from() {
      return (this.pagination.currentPage - 1) * this.pagination.perPage;
    },
    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
    total() {
      return this.filteredProducts.length;
    },
    startIndex() {
      return (this.pagination.currentPage - 1) * this.pagination.perPage;
    },
    endIndex() {
      return this.startIndex + this.pagination.perPage;
    },
  },
  methods: {
    async goToProduct(product) {
      this.$router.push(this.$objectViewRoute(product));
    },
    getProductUnitPrice(product) {
      if (product.warehouseProduct && product.warehouseProduct.buying_price) {
        return product.warehouseProduct.buying_price;
      }
      return product.buying_price || 0;
    },
    async getProducts({ force = false } = {}) {
      const signature = this.createProductsFetchSignature();
      if (!force && signature && this.productsSignatureLoaded === signature) {
        this.filterProducts();
        return;
      }
      if (
        !force &&
        this.loading &&
        signature &&
        this.lastProductsFetchSignature === signature
      ) {
        return;
      }

      const requestToken = Symbol("productsRequest");
      this.activeProductsRequestToken = requestToken;

      try {
        this.loading = true;
        this.lastProductsFetchSignature = signature;

        if (!this.transfer) {
          if (this.activeProductsRequestToken === requestToken) {
            this.loading = false;
            this.activeProductsRequestToken = null;
          }
          return;
        }

        const params = {
          sort: "-created_at",
          filter: {
            organization: this.transfer.organization?.id,
            warehouse: this.transfer.sourceEstablishment.warehouse?.id,
            can_be_purchased: true,
          },
          include:
            "taxGroups,unitOfMeasure,orderUnitOfMeasureUnit,categories,suppliers,replacements,unitOfMeasure.referenceUnit",
          page: {
            number: 1,
            size: 999999,
          },
        };

        await this.$store.dispatch("products/list", params);
        const productsArr = await this.$store.getters["products/list"];

        if (this.activeProductsRequestToken !== requestToken) {
          return;
        }

        this.products = productsArr;
        this.filterProducts();
        this.loading = false;
        this.lastProductsFetchSignature = signature;
        this.productsSignatureLoaded = signature;
        this.activeProductsRequestToken = null;
      } catch (error) {
        console.error(error);
        this.$notify({
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
          type: "error",
        });
        if (this.activeProductsRequestToken === requestToken) {
          this.loading = false;
          this.lastProductsFetchSignature = null;
          this.productsSignatureLoaded = null;
          this.activeProductsRequestToken = null;
        }
      }
    },
    createProductsFetchSignature() {
      if (!this.transfer) {
        return null;
      }
      const orgId = this.transfer.organization?.id || "";
      const sourceEstId = this.transfer.sourceEstablishment?.id || "";
      return `${orgId}::${sourceEstId}`;
    },
    syncTransferItems() {
      if (!this.transfer || !this.transfer.items) {
        return;
      }
      const itemsQuantities = {};
      const itemsByProduct = {};

      this.transfer.items.forEach((item) => {
        const storableId = item.storable?.id || item.storable_id;
        if (!storableId) {
          return;
        }
        itemsQuantities[storableId] = item.quantity;
        itemsByProduct[storableId] = item;
      });

      this.itemsQuantities = itemsQuantities;
      this.itemsByProduct = itemsByProduct;
      this.filterProducts();
    },
    updateTransferItem(product, quantity) {
      const parsedQty = parseFloat(quantity);
      if (isNaN(parsedQty) || parsedQty < 0) {
        this.resetProductQuantity(product);
        return;
      }

      let transferItemData = cloneDeep(defaultEstablishmentTransferItem);
      const foundItem = this.transfer.items.find((item) => {
        const storableId = item.storable?.id || item.storable_id;
        return storableId == product.id;
      });

      if (foundItem) {
        transferItemData = cloneDeep(foundItem);
      }

      if (foundItem && parsedQty === 0) {
        this.$emit("onDeleteTransferItem", foundItem.id);
        return;
      }

      transferItemData.transfer = {
        type: "establishment-transfers",
        id: this.transfer.id,
      };
      transferItemData.storable = {
        type: "products",
        id: product.id,
      };
      transferItemData.quantity = parsedQty;
      if (product.unitOfMeasure && product.unitOfMeasure.referenceUnit) {
        transferItemData.unitOfMeasureUnit =
          product.unitOfMeasure.referenceUnit;
      }
      this.$emit("onSubmitTransferItem", transferItemData);
    },
    resetProductQuantity(product) {
      const foundItem = this.transfer.items.find((item) => {
        const storableId = item.storable?.id || item.storable_id;
        return storableId == product.id;
      });
      if (foundItem) {
        this.itemsQuantities[product.id] = foundItem.quantity;
      } else {
        this.itemsQuantities[product.id] = 0;
      }
    },
    filterProducts() {
      const query = this.query;
      const augmentedProducts = [...(this.products || [])];
      const productIds = new Set(
        augmentedProducts
          .filter((product) => product && product.id)
          .map((product) => product.id)
      );

      Object.values(this.itemsByProduct || {}).forEach((item) => {
        const storable = item?.storable;
        if (!storable || !storable.id || productIds.has(storable.id)) {
          return;
        }
        const fallbackProduct = {
          ...storable,
          categories: storable.categories || item.categories || [],
          suppliers: storable.suppliers || item.suppliers || [],
          unitOfMeasure: storable.unitOfMeasure || item.unitOfMeasure || null,
          warehouseProduct:
            storable.warehouseProduct || item.warehouseProduct || null,
        };
        augmentedProducts.push(fallbackProduct);
        productIds.add(fallbackProduct.id);
      });

      const filteredData = augmentedProducts.filter((product) => {
        const matchesQuery =
          !query ||
          this.$unaccent(product.code || "")
            .toLowerCase()
            .includes(this.$unaccent(query).toLowerCase()) ||
          this.$unaccent(product.sku || "")
            .toLowerCase()
            .includes(this.$unaccent(query).toLowerCase()) ||
          this.$unaccent(product.name || "")
            .toLowerCase()
            .includes(this.$unaccent(query).toLowerCase());

        const quantity = this.itemsQuantities[product.id] || 0;
        const matchesQuantityFilter = this.displayZeroQuantities
          ? true
          : quantity > 0;

        const matchCategory =
          !this.selectedCategory ||
          product.categories?.some(
            (category) => category.id === this.selectedCategory
          );

        const matchSupplier =
          !this.selectedSupplier ||
          product.suppliers?.some(
            (supplier) => supplier.id === this.selectedSupplier
          );

        return (
          matchesQuery &&
          matchesQuantityFilter &&
          matchCategory &&
          matchSupplier
        );
      });

      this.filteredProducts = filteredData;
    },
  },
  watch: {
    transfer: {
      handler() {
        this.getProducts();
        this.syncTransferItems();
        this.displayZeroQuantities =
          (this.transfer.status || "DRAFT") === "DRAFT";
      },
      deep: true,
    },
    query: {
      handler() {
        this.filterProducts();
      },
      immediate: true,
      deep: true,
    },
    displayZeroQuantities: {
      handler() {
        this.filterProducts();
      },
      immediate: true,
      deep: true,
    },
    pagination: {
      handler() {
        this.filterProducts();
      },
      deep: true,
    },
    selectedCategory: {
      handler() {
        this.filterProducts();
      },
      deep: true,
    },
    selectedSupplier: {
      handler() {
        this.filterProducts();
      },
      deep: true,
    },
  },
};
</script>

<style>
.salesOrder-item-excerpt p,
.salesOrder-item-excerpt,
.salesOrder-item-excerpt * {
  font-size: 0.7rem;
}
</style>
